/**
 * Gestão de Pedidos — aba "Comprador" do detalhe (rodada 5, 2026-09-29).
 *
 * Histórico do MESMO comprador (buyer_id) para identificar possíveis
 * fraudadores: o ML mascara CPF e endereço, então só dá para cruzar a mesma
 * conta. `GET /pedidos/:id/comprador` →
 *   `{ buyer, summary, alert, orders, truncated }` (ver spec-rodada5).
 * A página carrega junto com o detalhe (um request a mais) para o chip de
 * alerta do cabeçalho; a aba só desenha o que já veio.
 *
 * O alerta ("Atenção: 3 reclamações nos últimos 180 dias") é ÂMBAR — não é
 * urgência e não bloqueia nada. Linguagem visual do módulo: plana, cantos
 * QUADRADOS, negrito só na identidade (apelido, nº do pedido).
 */
import { Link } from 'react-router-dom';
import { Warning } from 'shared/icons';
import { CleanPackId, OccurrenceTitle, OrderStatusPill } from '../../components/pedidos/pedidosShared';
import { fmtDate, Panel, StatusPill } from '../../components/rma/rmaShared';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { ML_CLAIM_TYPE_LABELS, MlLabel } from '../../constants/mlClaimCatalog';
import { BUYER_CASE_TYPE_PLURALS } from '../../constants/pedidoTags';
import { FormatInt, FormatMoney } from '../../utils/format';

const SECTION_LABEL = 'text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600';
const META_TEXT = 'text-[11px] text-mono-400 dark:text-mono-500';
const ALERT_CLASSES =
    'bg-amber-50 text-amber-900 ring-1 ring-inset ring-amber-600/40 dark:bg-amber-400/10 dark:text-amber-200 dark:ring-amber-400/40';
// Data | Pedido/Pack | Itens | Valor | Situação | Casos
const ORDER_GRID = 'grid-cols-[8.5rem_11rem_minmax(12rem,1fr)_6.5rem_8rem_minmax(14rem,18rem)]';
const ORDER_ROW_MIN = 'min-w-[64rem]';
const HEADER_CELL = 'text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600';

/** Texto do alerta: o `reason` do backend ou, sem ele, a regra. */
export function BuyerAlertText(alert) {
    if (!alert?.active) {
        return '';
    }
    if (alert.reason) {
        return `Atenção: ${alert.reason}`;
    }
    return `Atenção: ${FormatInt(alert.min_cases)} ou mais reclamações nos últimos ${FormatInt(alert.days)} dias`;
}

/**
 * Chip do alerta ao lado do comprador no cabeçalho do pedido — só com
 * `alert.active`. `onClick` abre a aba Comprador.
 */
export const BuyerAlertChip = ({ alert, onClick = null }) => {
    const text = BuyerAlertText(alert);
    if (!text) {
        return null;
    }
    const classes = `inline-flex items-center gap-1 whitespace-nowrap px-2 py-0.5 text-[11px] font-semibold ${ALERT_CLASSES}`;
    const content = (
        <>
            <Warning size={12} className="shrink-0" />
            {text}
        </>
    );
    if (!onClick) {
        return <span className={classes}>{content}</span>;
    }
    return (
        <button
            type="button"
            onClick={onClick}
            title="Ver o histórico do comprador"
            className={`${classes} transition-colors hover:bg-amber-100 dark:hover:bg-amber-400/20`}
        >
            {content}
        </button>
    );
};

/** `{ mediations: 3, returns: 1 }` → "3 reclamações · 1 devolução". Vazio → ''. */
export function CasesByTypeText(byType) {
    return Object.entries(byType && typeof byType === 'object' ? byType : {})
        .filter(([, count]) => Number(count) > 0)
        .map(([type, count]) => {
            const n = Number(count);
            const singular = MlLabel(ML_CLAIM_TYPE_LABELS, type).toLowerCase();
            const plural = BUYER_CASE_TYPE_PLURALS[type] || singular;
            return `${FormatInt(n)} ${n === 1 ? singular : plural}`;
        })
        .join(' · ');
}

/** Resumo curto da aba (linha de contexto do bloco "Comprador"). */
export function BuyerTabSub(state) {
    if (!state || (state.loading && !state.data)) {
        return { text: 'carregando…' };
    }
    if (state.error && !state.data) {
        return { text: 'indisponível agora' };
    }
    const data = state.data;
    if (!data?.buyer) {
        return { text: 'sem comprador identificado' };
    }
    if (data.alert?.active) {
        const cases = Number(data.summary?.cases_in_window) || 0;
        return { text: `alerta · ${FormatInt(cases)} ${cases === 1 ? 'caso' : 'casos'}`, tone: 'warn' };
    }
    const orders = Number(data.summary?.orders_total) || 0;
    return { text: `${FormatInt(orders)} ${orders === 1 ? 'pedido' : 'pedidos'}` };
}

const Stat = ({ label, children, sub = null }) => (
    <div className="min-w-0 bg-white px-4 py-2.5 dark:bg-mono-950">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-mono-400 dark:text-mono-600">{label}</p>
        <p className="truncate text-sm tabular-nums text-mono-900 dark:text-mono-100">{children}</p>
        {sub ? <p className={`truncate ${META_TEXT}`}>{sub}</p> : null}
    </div>
);

/** Itens do pedido: "SKU · título ×2" do primeiro e "+N" com o resto no title. */
const OrderItems = ({ items }) => {
    const list = Array.isArray(items) ? items : [];
    if (!list.length) {
        return <span className="text-xs text-mono-300 dark:text-mono-700">—</span>;
    }
    const [first, ...rest] = list;
    return (
        <span className="flex min-w-0 items-center gap-2">
            <span className="min-w-0 truncate text-[13px] text-mono-800 dark:text-mono-200" title={first.title || ''}>
                {first.sku ? (
                    <span className="mr-1.5 font-mono text-[11px] text-mono-500 dark:text-mono-400">{first.sku}</span>
                ) : null}
                {first.title || '—'}
                {Number(first.quantity) > 1 ? (
                    <span className="ml-1 text-[11px] text-mono-400 dark:text-mono-500">×{first.quantity}</span>
                ) : null}
            </span>
            {rest.length ? (
                <span
                    className="shrink-0 bg-mono-100 px-1.5 py-0.5 text-[11px] text-mono-500 dark:bg-mono-900 dark:text-mono-400"
                    title={rest.map((item) => [item.sku, item.title].filter(Boolean).join(' · ')).join('\n')}
                >
                    +{rest.length}
                </span>
            ) : null}
        </span>
    );
};

/** Casos do pedido: "Reclamação #x" + situação, um por linha. */
const OrderCases = ({ cases }) => {
    const list = Array.isArray(cases) ? cases : [];
    if (!list.length) {
        return <span className="text-xs text-mono-300 dark:text-mono-700">—</span>;
    }
    return (
        <span className="flex min-w-0 flex-col gap-1">
            {list.map((occ) => {
                const typeLabel = occ.claim_type ? MlLabel(ML_CLAIM_TYPE_LABELS, occ.claim_type) : null;
                const title = [
                    typeLabel,
                    occ.reason_detail || null,
                    occ.claim_date_created ? `aberta ${fmtDate(occ.claim_date_created)}` : null,
                ]
                    .filter(Boolean)
                    .join(' · ');
                return (
                    <span
                        key={occ.id || occ.external_claim_id}
                        className="flex min-w-0 items-center gap-1.5"
                        title={title}
                    >
                        <span className="truncate font-mono text-[11px] text-mono-700 dark:text-mono-200">
                            {occ.claim_type === 'cancel_purchase'
                                ? `Cancelamento #${occ.external_claim_id || String(occ.id || '').slice(-8)}`
                                : OccurrenceTitle({ ...occ, kind: occ.kind || kindOfClaimType(occ.claim_type) })}
                        </span>
                        <StatusPill status={occ.status} />
                    </span>
                );
            })}
        </span>
    );
};

/** `claim_type` do ML → universo do título ("Reclamação #x" / "Devolução #x"). */
function kindOfClaimType(claimType) {
    if (claimType === 'returns' || claimType === 'return') {
        return 'devolucao';
    }
    return 'reclamacao';
}

/**
 * Aba Comprador. `state` = `{ data, loading, error }` da carga feita pela
 * página; `onRetry` refaz. `backTo` preserva o "voltar à lista" ao abrir
 * outro pedido do comprador.
 */
export const CompradorTab = ({ state, onRetry = null, backTo = null }) => {
    const { data = null, loading = false, error = '' } = state || {};
    if (loading && !data) {
        return <LoadingSpinner className="py-16" />;
    }
    if (error && !data) {
        return (
            <Panel title="Comprador">
                <p className="text-sm text-mono-500 dark:text-mono-400">
                    Histórico do comprador indisponível agora{error ? ` — ${error}` : ''}.
                </p>
                {onRetry ? (
                    <div>
                        <button
                            type="button"
                            onClick={onRetry}
                            className="border border-mono-300 px-3 py-1 text-sm text-mono-600 transition-colors hover:border-black hover:text-black dark:border-mono-700 dark:text-mono-300 dark:hover:border-white dark:hover:text-white"
                        >
                            Tentar de novo
                        </button>
                    </div>
                ) : null}
            </Panel>
        );
    }
    if (!data?.buyer) {
        return (
            <Panel title="Comprador">
                <p className="text-sm text-mono-500 dark:text-mono-400">
                    Pedido sem comprador identificado (buyer_id) — não há histórico para cruzar.
                </p>
            </Panel>
        );
    }

    const { buyer, summary = null, alert = null, orders = [], truncated = false } = data;
    const list = Array.isArray(orders) ? orders : [];
    const linkState = backTo ? { from: backTo } : undefined;
    const byTypeText = CasesByTypeText(summary?.cases_by_type);
    const refundedAmount = summary?.refunded_amount;
    const alertText = BuyerAlertText(alert);

    return (
        <div className="flex min-w-0 flex-col gap-4">
            {/* ── Quem é: apelido (identidade), nome, buyer_id ── */}
            <section className="border border-mono-200 bg-white px-5 py-4 dark:border-mono-800 dark:bg-mono-950">
                <p className={SECTION_LABEL}>Comprador</p>
                <p className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="font-mono text-base font-bold text-black dark:text-white">
                        {buyer.nickname || '—'}
                    </span>
                    {buyer.name ? <span className="text-sm text-mono-700 dark:text-mono-200">{buyer.name}</span> : null}
                    <span className="font-mono text-[11px] text-mono-400 dark:text-mono-500">
                        ID {buyer.buyer_id || '—'}
                    </span>
                </p>
                <p className={`mt-1 ${META_TEXT}`}>
                    O Mercado Livre mascara CPF e endereço: o histórico cruza só a MESMA conta do comprador.
                </p>
            </section>

            {/* ── Alerta (âmbar, não bloqueia nada) ── */}
            {alertText ? (
                <div className={`flex flex-col gap-0.5 border-l-4 border-l-amber-500 px-4 py-2.5 ${ALERT_CLASSES}`}>
                    <p className="flex items-center gap-1.5 text-sm font-semibold">
                        <Warning size={15} className="shrink-0" />
                        {alertText}
                    </p>
                    <p className="text-[12px] opacity-80">
                        Possível comprador recorrente em reclamações — confira o histórico abaixo antes de decidir. Não
                        bloqueia nada.
                    </p>
                </div>
            ) : alert ? (
                <p className={META_TEXT}>
                    Sem alerta: {FormatInt(summary?.cases_in_window ?? 0)}{' '}
                    {Number(summary?.cases_in_window) === 1 ? 'caso' : 'casos'} nos últimos {FormatInt(alert.days)} dias
                    (o alerta acende a partir de {FormatInt(alert.min_cases)} — Regras).
                </p>
            ) : null}

            {/* ── Números ── */}
            {summary ? (
                <div className="grid grid-cols-2 gap-px border border-mono-200 bg-mono-200 dark:border-mono-800 dark:bg-mono-800 sm:grid-cols-4">
                    <Stat label="Pedidos">{FormatInt(summary.orders_total)}</Stat>
                    <Stat
                        label="1ª compra"
                        sub={summary.last_order_at ? `última ${fmtDate(summary.last_order_at)}` : null}
                    >
                        {fmtDate(summary.first_order_at)}
                    </Stat>
                    <Stat label="Total gasto">{FormatMoney(summary.total_amount)}</Stat>
                    <Stat label="Cancelados">{FormatInt(summary.cancelled_count)}</Stat>
                    <Stat label="Casos" sub={byTypeText || 'nenhum'}>
                        {FormatInt(summary.cases_total)}
                    </Stat>
                    <Stat label="Cancelamentos de compra">{FormatInt(summary.cancel_purchase_count)}</Stat>
                    <Stat
                        label="Reembolsos"
                        sub={refundedAmount === null || refundedAmount === undefined ? 'valor indisponível' : null}
                    >
                        {FormatInt(summary.refunded_count)}
                        {refundedAmount === null || refundedAmount === undefined
                            ? ''
                            : ` · ${FormatMoney(refundedAmount)}`}
                    </Stat>
                    <Stat label={`Casos em ${FormatInt(alert?.days ?? 0)} dias`}>
                        {FormatInt(summary.cases_in_window)}
                    </Stat>
                </div>
            ) : null}

            {/* ── Pedidos do comprador (mais recentes primeiro; o atual destacado) ── */}
            <section className="border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-950">
                <header className="flex flex-wrap items-center justify-between gap-2 border-b border-mono-200 px-5 py-2.5 dark:border-mono-800">
                    <h3 className="text-[11px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                        Pedidos do comprador · {FormatInt(list.length)}
                    </h3>
                    {truncated ? <span className={META_TEXT}>mostrando os 200 mais recentes</span> : null}
                </header>
                {list.length === 0 ? (
                    <p className="px-5 py-4 text-sm text-mono-500 dark:text-mono-400">Nenhum pedido no escopo.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <div
                            className={`grid ${ORDER_GRID} ${ORDER_ROW_MIN} items-center gap-x-3 border-b border-l-4 border-b-mono-200 border-l-transparent bg-mono-50 px-3 py-1.5 dark:border-b-mono-800 dark:bg-mono-900`}
                        >
                            <span className={HEADER_CELL}>Data</span>
                            <span className={HEADER_CELL}>Pedido / Pack</span>
                            <span className={HEADER_CELL}>Itens</span>
                            <span className={`${HEADER_CELL} text-right`}>Valor</span>
                            <span className={HEADER_CELL}>Situação</span>
                            <span className={HEADER_CELL}>Casos</span>
                        </div>
                        {list.map((order) => {
                            const cases = Array.isArray(order.cases) ? order.cases : [];
                            const pack = CleanPackId(order.pack_id);
                            const cells = (
                                <>
                                    <span className="text-xs tabular-nums text-mono-600 dark:text-mono-300">
                                        {fmtDate(order.date_created)}
                                    </span>
                                    <span className="flex min-w-0 flex-col leading-tight">
                                        <span className="truncate font-mono text-[13px] font-bold text-black dark:text-white">
                                            #{order.external_order_id}
                                        </span>
                                        {pack ? (
                                            <span className="truncate font-mono text-[11px] text-sky-700 dark:text-sky-300">
                                                pack {pack}
                                            </span>
                                        ) : null}
                                        {order.current ? (
                                            <span className="text-[10px] uppercase tracking-wider text-mono-500 dark:text-mono-400">
                                                este pedido
                                            </span>
                                        ) : null}
                                    </span>
                                    <OrderItems items={order.items} />
                                    <span className="text-right text-sm tabular-nums text-mono-800 dark:text-mono-200">
                                        {FormatMoney(order.total_amount)}
                                    </span>
                                    <span>
                                        <OrderStatusPill status={order.status} />
                                    </span>
                                    <OrderCases cases={cases} />
                                </>
                            );
                            const rowClass = `grid ${ORDER_GRID} ${ORDER_ROW_MIN} items-center gap-x-3 border-b border-l-4 border-b-mono-100 px-3 py-2 dark:border-b-mono-900`;
                            if (order.current) {
                                return (
                                    <div
                                        key={order.external_order_id}
                                        className={`${rowClass} border-l-black bg-mono-50 dark:border-l-white dark:bg-mono-900/60`}
                                    >
                                        {cells}
                                    </div>
                                );
                            }
                            const target = `/pedidos/${encodeURIComponent(order.external_order_id)}${
                                cases.length ? '?tab=posvenda' : ''
                            }`;
                            return (
                                <Link
                                    key={order.external_order_id}
                                    to={target}
                                    state={linkState}
                                    className={`${rowClass} ${
                                        cases.length ? 'border-l-amber-500' : 'border-l-transparent'
                                    } bg-white transition-colors hover:bg-mono-50 dark:bg-mono-950 dark:hover:bg-mono-900/60`}
                                >
                                    {cells}
                                </Link>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
};
